import { FC, useRef, useState } from 'react';
import { MAX_GIFT_CARDS, giftCardErrorMessage } from '../../application/giftCards';
import type { GiftCardItem } from '../../domain/models';
import { translate } from '../../i18n';
import { theme } from '../../theme';
import { giftCardPromoModalStyles } from './styles';
import giftCardModalIcon from '../../assets/gift-card-modal-icon.svg';
import closeIcon from '../../assets/icons/close.svg';

export interface GiftCardPromoModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Gift cards ya aplicadas al carrito (salen de la respuesta de backend). */
  giftCards: GiftCardItem[];
  /** Aplica el código en backend; si rechaza, el modal muestra el mensaje de error. */
  onApply: (redemptionCode: string) => Promise<void>;
  /** Quita la gift card (por su `id`) en backend. */
  onRemove: (giftCardId: string) => Promise<void>;
  testID?: string;
  accessibilityLabel?: string;
}

/** El placeholder no se puede estilar inline: color `#E5E5E5` del diseño, desde el token del tema. */
const INPUT_CLASS = 'gift-card-promo-modal-input';
const PLACEHOLDER_CSS = `.${INPUT_CLASS}::placeholder { color: ${theme.colors.palette.grey200}; opacity: 1; }`;

/**
 * GiftCardPromoModal — Bottom sheet para aplicar y quitar códigos de regalo.
 *
 * Cada código se aplica o se quita en backend al momento (`POST` / `DELETE .../carts/gift-cards`); la lista
 * refleja lo que devuelve el carrito. "Cancelar" y "Aceptar" cierran el modal: los cambios ya están hechos.
 * TODO(ENA-1638) [consultar]: si "Cancelar" debería deshacer los códigos aplicados en esta apertura.
 */
export const GiftCardPromoModal: FC<GiftCardPromoModalProps> = ({
  isOpen,
  onClose,
  giftCards,
  onApply,
  onRemove,
  testID = 'gift-card-promo-modal',
  accessibilityLabel = translate('paymentsUI.giftCard.modalLabel'),
}) => {
  const [codeInput, setCodeInput] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [isHoveredApply, setIsHoveredApply] = useState(false);
  const [isHoveredCancel, setIsHoveredCancel] = useState(false);
  const [isHoveredAccept, setIsHoveredAccept] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const canAddMoreCodes = giftCards.length < MAX_GIFT_CARDS;
  // "Aplicar" queda al 60 % de opacidad hasta que se escribe un código (Figma).
  const canApply = canAddMoreCodes && codeInput.trim().length > 0 && !busy;

  const run = async (action: () => Promise<void>): Promise<boolean> => {
    setBusy(true);
    try {
      await action();
      return true;
    } catch (failure) {
      setError(giftCardErrorMessage(failure));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleApplyCode = async () => {
    setError(undefined);

    const code = codeInput.trim().toUpperCase();
    if (!code) {
      setError(translate('paymentsUI.giftCard.errors.empty'));
      return;
    }
    if (giftCards.some((card) => card.redemptionCode.toUpperCase() === code)) {
      setError(translate('paymentsUI.giftCard.errors.duplicate'));
      return;
    }
    if (!canAddMoreCodes) {
      setError(translate('paymentsUI.giftCard.errors.maxCodes'));
      return;
    }

    // TODO(ENA-1638): el formato del código lo define backend; reducir esta validación local a "no vacío".
    if (!/^[A-Z0-9][A-Z0-9-]*$/.test(code)) {
      setError(translate('paymentsUI.giftCard.errors.invalidFormat'));
      return;
    }

    if (await run(() => onApply(code))) {
      setCodeInput('');
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const handleRemoveCode = async (giftCardId: string) => {
    setError(undefined);
    await run(() => onRemove(giftCardId));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void handleApplyCode();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={giftCardPromoModalStyles.overlay}
      data-testid={`${testID}-overlay`}
      onClick={busy ? undefined : onClose}
    >
      <style>{PLACEHOLDER_CSS}</style>
      <div
        style={giftCardPromoModalStyles.modal}
        data-testid={testID}
        role="dialog"
        aria-modal="true"
        aria-label={accessibilityLabel}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={giftCardPromoModalStyles.content} data-testid={`${testID}-content`}>
          <div style={giftCardPromoModalStyles.header}>
            <img
              src={giftCardModalIcon}
              alt=""
              height={48}
              style={giftCardPromoModalStyles.icon}
              data-testid={`${testID}-icon`}
            />

            <h2 style={giftCardPromoModalStyles.title} data-testid={`${testID}-title`}>
              {translate('paymentsUI.giftCard.label')}
            </h2>

            <p style={giftCardPromoModalStyles.description} data-testid={`${testID}-description`}>
              {translate('paymentsUI.giftCard.description')}
            </p>
          </div>

          <div style={giftCardPromoModalStyles.section} data-testid={`${testID}-section`}>
            <div style={giftCardPromoModalStyles.helperText} data-testid={`${testID}-helper`}>
              <span style={giftCardPromoModalStyles.helperIcon}>!</span>
              <p style={giftCardPromoModalStyles.helperTextLabel}>
                {translate('paymentsUI.giftCard.helperText', { max: MAX_GIFT_CARDS })}
              </p>
            </div>

            <div style={giftCardPromoModalStyles.inputGroup} data-testid={`${testID}-input-group`}>
              <input
                ref={inputRef}
                type="text"
                placeholder={translate('paymentsUI.giftCard.inputPlaceholder')}
                value={codeInput}
                onChange={(e) => {
                  setCodeInput(e.target.value);
                  setError(undefined);
                }}
                onKeyDown={handleKeyDown}
                disabled={!canAddMoreCodes || busy}
                className={INPUT_CLASS}
                style={{
                  ...giftCardPromoModalStyles.input,
                  opacity: canAddMoreCodes ? 1 : 0.6,
                  cursor: canAddMoreCodes ? 'text' : 'not-allowed',
                }}
                data-testid={`${testID}-input`}
                aria-label={translate('paymentsUI.giftCard.inputPlaceholder')}
              />
              <button
                type="button"
                style={{
                  ...giftCardPromoModalStyles.applyButton,
                  ...(isHoveredApply && canApply ? giftCardPromoModalStyles.applyButtonHover : {}),
                  ...(!canApply ? giftCardPromoModalStyles.applyButtonDisabled : {}),
                }}
                onClick={() => void handleApplyCode()}
                onMouseEnter={() => canApply && setIsHoveredApply(true)}
                onMouseLeave={() => setIsHoveredApply(false)}
                onTouchStart={() => canApply && setIsHoveredApply(true)}
                onTouchEnd={() => setIsHoveredApply(false)}
                disabled={!canApply}
                data-testid={`${testID}-apply-button`}
                aria-label={translate('paymentsUI.giftCard.applyButton')}
              >
                {translate('paymentsUI.giftCard.applyButton')}
              </button>
            </div>

            {error && (
              <div style={giftCardPromoModalStyles.errorBox} data-testid={`${testID}-error`} role="alert">
                <span style={giftCardPromoModalStyles.errorIcon}>✕</span>
                <p style={giftCardPromoModalStyles.errorText} data-testid={`${testID}-error-text`}>
                  {error}
                </p>
              </div>
            )}

            {giftCards.length > 0 && (
              <div style={giftCardPromoModalStyles.codesList} data-testid={`${testID}-codes-list`}>
                {giftCards.map((card, index) => (
                  <div
                    key={card.id}
                    style={giftCardPromoModalStyles.codeItem}
                    data-testid={`${testID}-code-item-${index}`}
                  >
                    <p
                      style={giftCardPromoModalStyles.codeText}
                      data-testid={`${testID}-code-text-${index}`}
                    >
                      {card.redemptionCode}
                    </p>
                    <button
                      type="button"
                      style={giftCardPromoModalStyles.removeCodeButton}
                      onClick={() => void handleRemoveCode(card.id)}
                      disabled={busy}
                      data-testid={`${testID}-remove-code-${index}`}
                      aria-label={translate('paymentsUI.giftCard.removeCode', { code: card.redemptionCode })}
                    >
                      <img src={closeIcon} alt="" width={24} height={24} style={{ display: 'block' }} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={giftCardPromoModalStyles.footer} data-testid={`${testID}-footer`}>
          <button
            type="button"
            style={{
              ...giftCardPromoModalStyles.cancelButton,
              ...(isHoveredCancel ? giftCardPromoModalStyles.cancelButtonHover : {}),
            }}
            onClick={onClose}
            disabled={busy}
            onMouseEnter={() => setIsHoveredCancel(true)}
            onMouseLeave={() => setIsHoveredCancel(false)}
            onTouchStart={() => setIsHoveredCancel(true)}
            onTouchEnd={() => setIsHoveredCancel(false)}
            data-testid={`${testID}-cancel-button`}
            aria-label={translate('paymentsUI.giftCard.cancelButton')}
          >
            {translate('paymentsUI.giftCard.cancelButton')}
          </button>
          <button
            type="button"
            style={{
              ...giftCardPromoModalStyles.acceptButton,
              ...(isHoveredAccept ? giftCardPromoModalStyles.acceptButtonHover : {}),
            }}
            onClick={onClose}
            disabled={busy}
            onMouseEnter={() => setIsHoveredAccept(true)}
            onMouseLeave={() => setIsHoveredAccept(false)}
            onTouchStart={() => setIsHoveredAccept(true)}
            onTouchEnd={() => setIsHoveredAccept(false)}
            data-testid={`${testID}-accept-button`}
            aria-label={translate('paymentsUI.giftCard.acceptButton')}
          >
            {translate('paymentsUI.giftCard.acceptButton')}
          </button>
        </div>
      </div>
    </div>
  );
};
